import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Input,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import CasinoIcon from '@mui/icons-material/Casino';
import RedeemIcon from '@mui/icons-material/Redeem';
import CelebrationIcon from '@mui/icons-material/Celebration';
import {
  DataGrid,
  deDE,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';

import { ProfileData } from '../../store/UserStore';
import { useStore } from '../../store/context';
import { addMaexchenCollectProductToGroup } from '../../firebaseConnection/database';
import {
  MaexchenCollectCreditWithId,
  RedeemedPointsWithId,
} from '../../store/PointsStore';

const UserLogDetailsTable = observer(
  ({ userItem }: { userItem: ProfileData | null }): JSX.Element => {
    const { authStore, redeemProductStore } = useStore();

    const ownUserId = authStore?.userData?.uid;
    const [addedMaexchen, setAddedMaexchen] = useState<string | null>(null);
    const [productDescription, setProductDescription] = useState('');
    const [open, setOpen] = useState(false);

    const getOrderItem = (element: RedeemedPointsWithId) => {
      const redeemEntry = redeemProductStore?.entriesArray.find(
        entry => entry.ProduktId === element.productId,
      );

      return (
        redeemEntry?.Produktname +
        (element.productVariantName ? ` (${element.productVariantName})` : '')
      );
    };

    const handleAddMaexchen = async () => {
      if (ownUserId && userItem?.userId && addedMaexchen) {
        if (userItem?.pointsStore.groupPoints + Number(addedMaexchen) < 0) {
          setOpen(true);
          return;
        }

        await addMaexchenCollectProductToGroup(
          ownUserId,
          userItem?.userId,
          Number(addedMaexchen),
          productDescription,
        );
      }
    };
    const isRedeemedCredit = (
      composite: MaexchenCollectCreditWithId | RedeemedPointsWithId,
    ): composite is RedeemedPointsWithId =>
      !!(composite as RedeemedPointsWithId).totalCredits;

    const convertTimestampToDate = (timestamp: number) => {
      const date = new Date(timestamp);
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    };

    const getRowIcon = (entryType: string) => {
      switch (entryType) {
        case 'redeemed':
          return <RedeemIcon />;
        case 'added':
          return <CelebrationIcon />;
        case 'collected':
          return <CasinoIcon />;
        default:
          return <CasinoIcon />;
      }
    };

    const columns: GridColDef[] = [
      {
        field: 'timestamp',
        headerName: 'Datum',
        width: 100,
        editable: false,
      },
      {
        field: 'log',
        headerName: 'Buchung',
        width: 250,
        editable: false,
        renderCell: cellValues => (
          <Box sx={{ display: 'flex', justifyItems: 'center' }}>
            <IconButton sx={{ marginRight: 1 }}>
              {getRowIcon(cellValues.row.type)}
            </IconButton>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyItems: 'center',
              }}
            >
              <Typography variant="body2">{cellValues.row.log}</Typography>

              <Typography variant="body2">
                {cellValues.row.collectCode}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        field: 'productDescription',
        headerName: 'Produktbeschreibung',
        width: 350,
        editable: false,
      },
      {
        field: 'user',
        headerName: 'User',
        width: 150,
        editable: false,
      },
    ];

    const renderUserDetails = () => {
      if (userItem) {
        if (userItem?.pointsStore.allEntries.length) {
          const transformedArray = userItem.pointsStore.allEntries.map(
            logItem => {
              if (isRedeemedCredit(logItem)) {
                return {
                  id: logItem.id,
                  timestamp: convertTimestampToDate(logItem.timestamp),
                  log: `Eingelöst: ${logItem.totalCredits} Mäxchen`,
                  productDescription: getOrderItem(logItem),
                  type: 'redeemed',
                  user: `${userItem.firstName} ${userItem.lastName}`,
                };
              }

              if (logItem.addedExternally === true) {
                return {
                  id: logItem.id,
                  timestamp: convertTimestampToDate(logItem.timestamp),
                  log: `Hinzugefügt: ${logItem.credits} Mäxchen`,
                  type: 'added',
                  user: logItem?.adminUserEmail ?? 'Admin',
                };
              }
              return {
                id: logItem.id,
                timestamp: convertTimestampToDate(logItem.timestamp),
                log: `Gesammelt: ${logItem.credits} Mäxchen`,
                productDescription: logItem.productDescription,
                type: 'collected',
                collectCode: logItem.collectCode,
                user: !logItem.subuserFirstName
                  ? `${userItem.firstName} ${userItem.lastName}`
                  : `${logItem.subuserFirstName} ${logItem.subuserLastName}`,
              };
            },
          );
          return (
            <DataGrid
              style={{ height: 400, minWidth: 800 }}
              localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
              components={{
                Toolbar: () => (
                  <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
                    <GridToolbarExport />
                    <GridToolbarQuickFilter />
                  </GridToolbarContainer>
                ),
              }}
              checkboxSelection={false}
              rows={transformedArray || []}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
              }}
              pageSizeOptions={[10]}
              disableRowSelectionOnClick
            />
          );
        }

        return (
          <Typography variant="body2">
            Dieser User hat noch keine Mäxchen gesammelt
          </Typography>
        );
      }
      return (
        <Typography variant="body2">Bitte wählen Sie einen User aus</Typography>
      );
    };

    return (
      <Box
        display="flex"
        flexDirection="column"
        style={{ height: 800, minWidth: 800, padding: '0 10px' }}
      >
        <Box style={{ width: '100%' }}>
          <TextField
            placeholder="Buchungstext"
            label="Buchungstext"
            variant="standard"
            sx={{ margin: 1 }}
            onChange={e => {
              setProductDescription(e.target.value);
            }}
          />
          <TextField
            label="Gesamtpunkte"
            disabled
            sx={{ margin: 1 }}
            variant="standard"
            InputLabelProps={{
              shrink: true,
            }}
            value={userItem?.pointsStore.groupPoints ?? 0}
          />
          <Box display="flex" flexDirection="row" style={{ margin: '10px 0' }}>
            <Input
              style={{ width: 100 }}
              type="number"
              placeholder="Punkte"
              onChange={event => setAddedMaexchen(event.target.value)}
            />
            <Button
              variant="contained"
              disabled={!addedMaexchen}
              onClick={handleAddMaexchen}
            >
              Hinzufügen
            </Button>
          </Box>
          <Snackbar
            open={open}
            autoHideDuration={6000}
            onClose={() => setOpen(false)}
            message="Negativer Punktestand nicht erlaubt!"
          />
        </Box>
        {renderUserDetails()}
      </Box>
    );
  },
);

export default UserLogDetailsTable;
